import React from "react";
import {
  CheckupRiskItem,
  ClinicalRiskItem,
  HealthThreatData,
  MedicationWarningItem,
} from "../../types/health-threat";
import { Button } from "../catalyst/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../catalyst/dialog";
import Icon from "../ui/Icon";

interface HealthThreatModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: HealthThreatData | null;
  isLoading: boolean;
  error: string | null;
}

const RISK_LEVEL_STYLES = {
  high: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
    badge: "bg-red-100 text-red-700",
    label: "높음",
  },
  moderate: {
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    text: "text-yellow-700",
    badge: "bg-yellow-100 text-yellow-700",
    label: "보통",
  },
  low: {
    bg: "bg-green-50",
    border: "border-green-200",
    text: "text-green-700",
    badge: "bg-green-100 text-green-700",
    label: "낮음",
  },
};

const WARNING_TYPE_LABELS: Record<MedicationWarningItem["warningType"], string> =
  {
    side_effect: "부작용",
    interaction: "상호작용",
    polypharmacy: "다제약물",
  };

const CLINICAL_CATEGORY_LABELS: Record<ClinicalRiskItem["category"], string> = {
  condition: "진단",
  procedure: "수술/시술",
  allergy: "알레르기",
};

const SectionHeader: React.FC<{ title: string; icon: string; count: number }> = ({
  title,
  icon,
  count,
}) => (
  <div className="flex items-center space-x-2 mb-2">
    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
      <Icon name={icon} size="sm" className="text-blue-600" />
    </div>
    <h3 className="text-xs font-semibold text-text-primary">{title}</h3>
    {count > 0 && (
      <span className="text-[10px] bg-gray-100 text-text-tertiary rounded-full px-1.5 py-0.5">
        {count}
      </span>
    )}
  </div>
);

const EmptySection: React.FC<{ message: string }> = ({ message }) => (
  <p className="text-xs text-text-tertiary py-1 pl-8">{message}</p>
);

const CheckupRiskCard: React.FC<{ risk: CheckupRiskItem }> = ({ risk }) => {
  const style = RISK_LEVEL_STYLES[risk.riskLevel];
  return (
    <div className={`${style.bg} border ${style.border} rounded-lg px-3 py-2`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-semibold text-text-primary">
          {risk.category}
        </span>
        <span
          className={`text-[10px] font-medium ${style.badge} rounded-full px-2 py-0.5`}
        >
          {style.label}
        </span>
      </div>
      <div className="flex items-baseline space-x-2 mb-0.5">
        <span className={`text-xs font-medium ${style.text}`}>
          {risk.currentValue}
        </span>
        <span className="text-[10px] text-text-tertiary">
          (참고: {risk.referenceRange})
        </span>
      </div>
      <p className="text-[11px] text-text-secondary">{risk.description}</p>
    </div>
  );
};

const MedicationWarningCard: React.FC<{ warning: MedicationWarningItem }> = ({
  warning,
}) => {
  const isInteraction = warning.warningType === "interaction";
  const bgColor = isInteraction ? "bg-red-50" : "bg-orange-50";
  const borderColor = isInteraction ? "border-red-200" : "border-orange-200";
  const iconColor = isInteraction ? "text-red-500" : "text-orange-500";

  return (
    <div className={`${bgColor} border ${borderColor} rounded-lg px-3 py-2`}>
      <div className="flex items-start space-x-2">
        <Icon name="alertTriangle" size="sm" className={`${iconColor} mt-0.5 flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-1.5 mb-0.5">
            <span className="text-[10px] font-medium bg-gray-200 text-text-secondary rounded px-1.5 py-0.5">
              {WARNING_TYPE_LABELS[warning.warningType]}
            </span>
          </div>
          <p className="text-[11px] text-text-secondary mb-1">
            {warning.description}
          </p>
          <div className="flex flex-wrap gap-1">
            {warning.medications.map((med, idx) => (
              <span
                key={idx}
                className="text-[10px] bg-white border border-gray-200 text-text-primary rounded px-1.5 py-0.5"
              >
                {med}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ClinicalRiskCard: React.FC<{ risk: ClinicalRiskItem }> = ({ risk }) => {
  const categoryColors: Record<ClinicalRiskItem["category"], string> = {
    condition: "bg-purple-100 text-purple-700",
    procedure: "bg-blue-100 text-blue-700",
    allergy: "bg-red-100 text-red-700",
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
      <div className="flex items-center space-x-1.5 mb-1">
        <span
          className={`text-[10px] font-medium ${categoryColors[risk.category]} rounded px-1.5 py-0.5`}
        >
          {CLINICAL_CATEGORY_LABELS[risk.category]}
        </span>
        <span className="text-[11px] font-semibold text-text-primary">
          {risk.name}
        </span>
        {risk.date && (
          <span className="text-[10px] text-text-tertiary">({risk.date})</span>
        )}
      </div>
      <p className="text-[11px] text-text-secondary">{risk.description}</p>
    </div>
  );
};

const HealthThreatModal: React.FC<HealthThreatModalProps> = ({
  isOpen,
  onClose,
  data,
  isLoading,
  error,
}) => {
  const isEmpty =
    data &&
    data.checkupRisks.length === 0 &&
    data.medicationWarnings.length === 0 &&
    data.clinicalRisks.length === 0;

  return (
    <Dialog open={isOpen} onClose={onClose} size="lg">
      <div className="flex items-center justify-between">
        <DialogTitle>내 건강 위험 & 관리</DialogTitle>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Icon name="x" size="md" className="text-text-secondary" />
        </button>
      </div>

      <DialogBody>
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <svg
              className="w-8 h-8 text-blue-500 animate-spin mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-sm text-text-secondary">
              건강 위험도를 분석하고 있습니다...
            </p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Icon name="x" size="lg" className="text-red-600" />
            </div>
            <p className="text-sm text-text-secondary">{error}</p>
          </div>
        )}

        {!isLoading && !error && isEmpty && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Icon name="shield" size="lg" className="text-gray-400" />
            </div>
            <p className="text-sm text-text-secondary">
              분석할 의료 데이터가 없습니다.
            </p>
            <p className="text-xs text-text-tertiary mt-1">
              설정에서 내 의료기록을 연동해주세요.
            </p>
          </div>
        )}

        {!isLoading && !error && data && !isEmpty && (
          <div className="space-y-4">
            {/* HR-01: 건강검진 기반 위험도 */}
            <div>
              <SectionHeader
                title="건강검진 기반 위험도"
                icon="chart"
                count={data.checkupRisks.length}
              />
              {data.checkupRisks.length === 0 ? (
                <EmptySection message="건강검진 데이터에서 특이사항이 없습니다." />
              ) : (
                <div className="space-y-2">
                  {data.checkupRisks.map((risk, idx) => (
                    <CheckupRiskCard key={`checkup-${idx}`} risk={risk} />
                  ))}
                </div>
              )}
            </div>

            {/* HR-02: 투약 기반 주의사항 */}
            <div>
              <SectionHeader
                title="투약 기반 주의사항"
                icon="activity"
                count={data.medicationWarnings.length}
              />
              {data.medicationWarnings.length === 0 ? (
                <EmptySection message="투약 관련 주의사항이 없습니다." />
              ) : (
                <div className="space-y-2">
                  {data.medicationWarnings.map((warning, idx) => (
                    <MedicationWarningCard
                      key={`med-${idx}`}
                      warning={warning}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* HR-03: 검사/수술 기반 위험 */}
            <div>
              <SectionHeader
                title="검사/수술 기반 주의사항"
                icon="clipboard"
                count={data.clinicalRisks.length}
              />
              {data.clinicalRisks.length === 0 ? (
                <EmptySection message="검사/수술 관련 주의사항이 없습니다." />
              ) : (
                <div className="space-y-2">
                  {data.clinicalRisks.map((risk, idx) => (
                    <ClinicalRiskCard key={`clinical-${idx}`} risk={risk} />
                  ))}
                </div>
              )}
            </div>

            {data.generatedAt && (
              <p className="text-xs text-text-tertiary text-right">
                분석일시:{" "}
                {new Date(data.generatedAt).toLocaleString("ko-KR")}
              </p>
            )}
          </div>
        )}
      </DialogBody>

      <DialogActions>
        <Button outline onClick={onClose}>
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HealthThreatModal;
